import { addListener, isAnyOf } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

/**
 * Custom hook to execute a callback when any of the given matchers matches an action.
 *
 * Scenarios where this can be useful (without the obvious ones like showing notifications or redirecting):
 *  - After creating/updating/deleting an item, refetch the list to show the updated data
 *  - After failing to create/update/delete an item, log the error
 *
 * This is a base hook used by other hooks like useNavigateOnMatch and useNotificationOnMatch,
 * which provide common callbacks for navigation and notifications.
 *
 * The callback receives the matched action and listenerApi as arguments, so it can perform
 * side effects or dispatch (listenerApi.dispatch) further actions as needed.
 *
 * Note on matchers:
 * Matchers can be action types, action creators or custom matcher functions.
 * If an action creator is provided, we will check for its .fulfilled action type,
 * e.g. createItem instead of createItem.fulfilled.
 *
 * Example usage:
 *
 * useCallbackOnMatch(
 *  (action, listenerApi) => { .... },
 *  createItem.rejected,
 *  updateItem.pending,
 *  deleteItem.fulfilled
 * );
 *
 * Matchers can be action types, action creators or custom matcher functions.
 * See https://redux-toolkit.js.org/api/matching-utilities
 *
 * @param {function} effect - callback to execute when any of the matchers matches an action, receives the matched action and listenerApi as arguments
 * @param  {...any} $matchers - matchers can be action types, action creators or custom matcher functions
 */
export default function useCallbackOnMatch(effect, ...$matchers) {
  const dispatch = useDispatch();

  if (!effect || typeof effect !== "function") {
    throw new Error("useCallbackOnMatch: callback must be a function");
  }

  if ($matchers.length === 0) {
    throw new Error(
      "useCallbackOnMatch: no matchers provided, expected one or more action types, action creators or custom matcher functions"
    );
  }

  //
  const matcher = isAnyOf(...$matchers);

  // see https://redux-toolkit.js.org/api/createListenerMiddleware#adding-listeners-inside-components
  // for on implementation details and rationale behind this approach
  useEffect(() => {
    const unsubscribe = dispatch(addListener({ effect, matcher }));
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [dispatch, effect, matcher]);
}
